import type { AlgoliaProductHit } from '~/types/algolia';

import { localisedData } from '.';

type TransformProductParams = {
  hit: AlgoliaProductHit;
  lang: string;
  priceFormatter: (value: number) => string;
  cloudinaryURL1: string;
  defaultCurrency?: string | null;
};

export const transformProduct = ({
  hit,
  lang,
  priceFormatter,
  defaultCurrency,
  cloudinaryURL1,
}: TransformProductParams) => ({
  name: localisedData(hit, hit.name, lang),
  url: hit.url,
  color: localisedData(hit, hit.exactColorDescription, lang),
  code: hit.code,
  materialGroup: localisedData(hit, hit.materialgroup, 'EN'),
  baseProduct: hit['baseProduct.code'],
  signings: hit.multiSigningJson.map(signing => ({
    ...signing,
    image: {
      url: signing.image,
    },
  })),
  price: {
    value: hit.priceValue,
    formattedValue: priceFormatter(hit.priceValue),
    currencyIso: defaultCurrency,
  },
  personalisedPrice: hit.personalisedPriceValue
    ? {
        value: hit.personalisedPriceValue,
        formattedValue: priceFormatter(hit.personalisedPriceValue),
        currencyIso: defaultCurrency,
      }
    : null,
  fromPrice: hit.fromPriceValue
    ? {
        value: hit.fromPriceValue,
        formattedValue: priceFormatter(hit.fromPriceValue),
        currencyIso: defaultCurrency,
      }
    : {},
  intermediaryPrice: hit.intermediaryPriceValue
    ? {
        value: hit.intermediaryPriceValue,
        formattedValue: priceFormatter(hit.intermediaryPriceValue),
        currencyIso: defaultCurrency,
      }
    : {},
  formattedLowestPrice: hit.lowestPrice ? priceFormatter(hit.lowestPrice) : null,
  simplifiedImages: hit.simplifiedImageJson
    ? {
        entry: Object.entries(hit.simplifiedImageJson).map(([key, value]) => ({
          key,
          value: {
            ...value,
            url: value.url
              ? `${cloudinaryURL1}c_fill,f_auto,{{dimensions}}/${value.url}`
              : undefined,
          },
        })),
      }
    : {},
  fiberRanking: {
    fiberRanking: {
      productFiberRankingData: {
        score: hit.fiberRankingScore,
        ranking: hit.fiberRanking,
        fibers: hit.fiberCompositionsJson,
      },
    },
  },
  stockQuantity: hit.stockQuantity,
  mccValue: hit.mccValue,
  // props bellow are needed for the datalayer
  stockNotificationMessageEN: localisedData(hit, hit.stockNotificationMessage, 'EN'),

  baseProductStockNotificationMessageEN: localisedData(
    hit,
    hit['baseProduct.stockNotificationMessage'],
    'EN'
  ),
  ean: hit.ean,
  oneSize: hit.oneSize,
  isGiftCard: hit.isGiftCard,
  size: {
    formattedValue: localisedData(hit, hit.jeansSize, 'EN'),
    value: localisedData(hit, hit.size, 'EN'),
  },
  fitEN: localisedData(hit, hit.fit, 'EN'),
  mainColor: {
    descriptionEN: localisedData(hit, hit.colour, 'EN'),
    facetColorStyle: hit.facetColorStyle,
  },
  nameEN: localisedData(hit, hit.name, 'EN'),
  programEN: localisedData(hit, hit.program, 'EN'),
  staticCategoryPath: hit.staticCategoryPath,
  staticCategoryPathIds: hit.staticCategoryPathIds,
  styleFamilyEN: localisedData(hit, hit.style, 'EN'),
  subTargetAudience: hit.subTargetAudience,
  comingSoon: hit.comingSoon,
  algoliaObjectId: hit.objectID,
});
